import { Box, Stack } from "@mui/material"
import { TEditForm } from "../types"
import { Button, Chip, deleteStorageItem, DialogHandler, EEditorType, EmptyContent, EnhancedFormModel, EStorageType, fetchRoute, FormsFactory, FullBar, GenericWrapperContext, getBrandUrl, Header, Icon, initFieldValidation, InlineTextEdit, isValidUrl, ListModel, ListsFactory, LoadingContainer, Modal, ResourceEdit, TCopyToClipboard, TEnhancedFormModel, TextField, TFieldValidation, TInlineTextEditActions, TListCustomAttribute, TListModel, truncateText, Typography, validateGenericInput, validateUrl } from "@cakemail-org/ui-components-v2"
import { Trans, useTranslation } from "react-i18next"
import { useContext, useState, useEffect, ChangeEventHandler, ChangeEvent, useRef } from "react"
import { ERoutes } from "../../../utils/url"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import { useLocation, useMatches, useParams, useSearchParams } from "react-router-dom"
import BeeEditor from "../../../components/beeEditor"
import { ListSearch } from "../../../components/searchDropdowns/ListSearch"
import { logError } from "../../../utils/data"
import { ResourcePreview } from "../../../components/Resources/preview"
import "../styles.scss"
import { OptInPolicy } from "../../../components/forms/optInPolicy/OpinPolicy"
import { ResourceCopy } from "../../../components/Resources/copy"
import { ELogErrorMsg } from "../../../utils/data/types"
import { EEditorCacheKeys } from "../../../components/beeEditor/types"
import { buildDefaultUrls, buildFromScript, buildLink, updateFormLayoutOptions } from "../utils"
import { getBaseForm } from "../../../components/beeEditor/baseContent/baseForm"
import { AddTagsButton } from "../../../components/Tags"


export default function FormDetails({ }: TEditForm) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()
    const { formId } = useParams()

    const isNewForm = formId === "new"

    const location = useLocation()

    const [isEditing, setIsEditing] = useState<boolean>(location.pathname.split('/').pop() === 'edit')

    const [form, setForm] = useState<EnhancedFormModel | undefined>(isNewForm ? new EnhancedFormModel({
        name: t("general-untitled"),
        content: {
            json: getBaseForm()
        },
        list_id: 0,
        post_redirect_url: "",
        double_opt_in_redirect_url: "",
        double_opt_in: true,
        branding: "smart",
        tags: null
    } as TEnhancedFormModel) : undefined)

    const [fieldStates, setFieldStates] = useState<Record<string, TFieldValidation>>({
        ...initFieldValidation("post_redirect_url", { value: form?.post_redirect_url }),
        ...initFieldValidation("double_opt_in_redirect_url", { value: form?.double_opt_in_redirect_url })
    })

    const [previewContent, setPreviewContent] = useState<string | undefined>(isNewForm ? "" : undefined)

    const [selectedList, setSelectedList] = useState<ListModel>()


    useEffect(() => {
        if (formId && !isNewForm) {
            fetchForm().then((response) => {
                if (response instanceof EnhancedFormModel) {
                    if (response?.list_id) {
                        ListsFactory.get({ id: response.list_id })
                            .then((list) => {
                                setSelectedList(list)
                            })
                    }
                }
            })
        }
    }, [])

    useEffect(() => {
        return () => {
            deleteStorageItem({ key: EEditorCacheKeys.listAttributes, type: EStorageType.session })
            deleteStorageItem({ key: EEditorCacheKeys.listInterests, type: EStorageType.session })
            deleteStorageItem({ key: EEditorCacheKeys.tags, type: EStorageType.session })
        }
    }, [])


    async function fetchForm() {
        return FormsFactory.get({ id: formId || "" })
            .then(async (response) => {
                if (response instanceof EnhancedFormModel) {
                    const cForm = new EnhancedFormModel(response)
                    cForm.content.json = await updateFormLayoutOptions({ form: cForm, listId: cForm.list_id })
                    setForm(cForm)
                    renderForm(cForm)
                    return cForm
                } else {
                    window.location.href = "#" + fetchRoute({ route: ERoutes.formEditBasic, params: { formId: formId } })
                    return response
                }
            }).catch((error) => {
                DialogHandler.show({
                    type: "warning",
                    title: t("general-error-msg"),
                })
                window.location.href = "#" + fetchRoute({ route: ERoutes.forms })
            })
    }

    async function renderForm(cForm?: EnhancedFormModel) {
        const formToRender = cForm || form
        if (formToRender) {
            formToRender.render().then((content) => {
                setPreviewContent(content)
            }).catch((error) => {
                if (error?.code === 1507) {
                    setPreviewContent("")
                }
            })
        }
    }



    function handlePublish(e: React.MouseEvent<HTMLButtonElement>, btn: TButtonRef) {
        if (form) {
            form.publish().then((response) => {
                DialogHandler.show({
                    type: "success",
                    title: t("general-published"),
                })
                setForm(new EnhancedFormModel(response))
            }).catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action"),
                })
                logError(ELogErrorMsg.ErrorPublishingForm, error)
            }).finally(() => {
                btn.reset()
            })
        }
    }

    async function handleBrandingToggle() {
        if (form) {
            form.branding = form.branding === "smart" ? "basic" : "smart"
            return saveForm(form).then((data) => {
                if (data instanceof EnhancedFormModel) {
                    renderForm(data)
                    return data?.content?.json || null
                }
            })
        }
    }

    function handleFirstListLoaded(list: ListModel) {
        if (isNewForm) {
            handleListChange(list)
        }
    }

    function handleListChange(list: ListModel) {
        if (form) {
            form.list_id = list.id
            saveForm(form)
            setSelectedList(list)
        }
    }

    function handleRedirectionChangeTimeout(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const { name, value } = event.target
        if (form) {
            form[name as "post_redirect_url" | "double_opt_in_redirect_url"] = value
            setFieldStates({
                ...fieldStates,
                [name]: validateUrl(value)
            })
            saveForm(form)
        }
    }

    function handleInlineFieldConfirm({ name, value }: { name?: string, value?: string }) {
        if (form) {
            form[name as "name"] = value || ""
            if (form.list_id === 0) {
                setForm(new EnhancedFormModel(form))
            } else {
                saveForm(form)
            }
        }
    }

    function handleOptInPolicyChange(event: ChangeEvent<HTMLInputElement>, value: string) {
        if (form) {
            form.double_opt_in = value === "dOptIn"
            saveForm(form)
        }
    }


    function handleTag(tag: string, btn: TButtonRef) {
        if (form) {
            form.tags = [...(form.tags || []), tag]
            saveForm(form).finally(() => {
                btn.reset()
            })
        }
    }

    function handleUntag(tag: string, btn: TButtonRef) {
        if (form) {
            form.tags = (form.tags || []).filter((t) => t !== tag)
            saveForm(form).finally(() => {
                btn.reset()
            })
        }
    }


    async function handleOnEditorSave(jsonFile: string, htmlFile: string) {
        if (form) {
            form.content.json = JSON.parse(jsonFile)
            return saveForm(form).then(() => {
                renderForm()
            })
        }
    }

    function handleOnClose() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.forms })
    }

    function handleFormEdit() {
        setIsEditing(true)
        window.location.href = "#" + fetchRoute({ route: ERoutes.formEdit, params: { formId: formId } })
    }

    function handleOnEditorClose() {
        setIsEditing(false)
        window.location.href = "#" + fetchRoute({ route: ERoutes.formDetails, params: { formId: formId } })
    }

    async function saveForm(cForm?: EnhancedFormModel) {
        const formToSave = cForm || form
        if (formToSave && formToSave.list_id > 0) {
            if (formToSave.name == "" ||
                (formToSave.post_redirect_url && fieldStates.post_redirect_url.error) ||
                (formToSave.double_opt_in_redirect_url && fieldStates.double_opt_in_redirect_url.error)
            ) {
                return
            }

            const defaultUrls = buildDefaultUrls({ list: selectedList, account: genericWrapper.account, user: genericWrapper.user, form: formToSave })

            if (formToSave.post_redirect_url === "" || formToSave.post_redirect_url?.includes("undefined")) {
                formToSave.post_redirect_url = defaultUrls.post_redirect_url
            }

            if (formToSave.double_opt_in_redirect_url === "" || formToSave.double_opt_in_redirect_url?.includes("undefined")) {
                formToSave.double_opt_in_redirect_url = defaultUrls.double_opt_in_redirect_url
            }

            if (isNewForm) {
                return FormsFactory.create({ form: formToSave.toJson(), type: "enhanced" }).then((response) => {
                    window.location.href = "#" + fetchRoute({ route: ERoutes.formDetails, params: { formId: response.id } })
                    setForm(new EnhancedFormModel(response as EnhancedFormModel))
                    DialogHandler.show({
                        type: "success",
                        title: t("general-form-created"),
                    })
                    renderForm(new EnhancedFormModel(response as EnhancedFormModel))
                    return response
                }).catch((error) => {
                    DialogHandler.show({
                        type: "error",
                        title: t("general-error-action"),
                    })
                    window.location.href = "#" + fetchRoute({ route: ERoutes.forms })
                    logError(ELogErrorMsg.ErrorCreatingForm, error)
                })
            }
            return formToSave.save().then((response) => {
                setForm(new EnhancedFormModel(response))
                return response
            }).catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action"),
                })
                logError(ELogErrorMsg.ErrorSavingForm, error)
            })
        }
    }

    const isPublished = form && form?.published_on > 0
    const hasUnpublishedChanges = form && ((form?.published_on > 0 && form?.updated_on - 1 > form?.published_on) || form?.published_on == null)

    const isDefaultPostUrl = form?.post_redirect_url === "" || form?.post_redirect_url?.includes(fetchRoute({ route: ERoutes.listSubscription }))
    const isDefaultDoptInUrl = form?.double_opt_in_redirect_url === "" || form?.double_opt_in_redirect_url?.includes(fetchRoute({ route: ERoutes.listConfirmation }))


    return <Modal className={"editFormModal"}
        open
        heading={{
            logoUrl: getBrandUrl({ brand: genericWrapper.partnerBrand, elKey: "logo" }),
            titleContent: <>
                <Typography variant="title" color="body1.main">{t("general-form_one")}</Typography>
                {form && <Chip label={t(isPublished ? "general-published" : "general-unpublished")} color={isPublished ? "secondary" : "primary"} />}
            </>,
            actions: [
                <Button key="publish" animated disabled={!form} color="secondary" variant='contained' startIcon={<Icon name={hasUnpublishedChanges ? "Publish18" : "Checkmark14"} />} onClick={handlePublish} >
                    {t(hasUnpublishedChanges ? "general-publish" : "general-published")}
                </Button>
            ],
            closeBtnText: t("general-close"),
            closeBtnOnClick: handleOnClose
        }}
        content={
            <LoadingContainer loading={!form}>
                {form && <Stack className="editFormContainer" direction="row">
                    <Stack className="editFormDataContainer" gap={6}>
                        <FullBar
                            left={
                                <Typography variant="title" color="primary.main">
                                    <InlineTextEdit
                                        name="name"
                                        tooltipTitle={t("general-form_name")}
                                        onConfirm={handleInlineFieldConfirm}
                                        defaultValue={form.name}
                                        placeholder={t("general-untitled")}
                                    />
                                </Typography>

                            }
                            right={[
                                <Typography key="tagCount" variant="body1" className="semiBold">
                                    {form.tags?.length || 0} {t("general-tag", { count: form.tags?.length || 0 })}
                                </Typography>,
                                <AddTagsButton tagList={form.tags || []} onTag={handleTag} onUntag={handleUntag} />
                            ]}
                        />

                        <ResourceEdit
                            initOpen
                            title={t("general-audience_one")}
                            desc={t("form-audience-desc")}
                            support={<Typography variant="body1">{t("form-audience-support")} <b>'{selectedList?.name || ""}'</b></Typography>}
                            editableContent={<ListSearch initValue={form.list_id} onFirstListLoaded={handleFirstListLoaded} onChange={handleListChange} />}
                        />

                        <ResourceEdit
                            editable
                            title={t("general-opt-in-settings")}
                            desc={t("form-opt-in-desc")}
                            support={<Typography variant="body1"><Trans i18nKey={form.double_opt_in ? "form-opt-in-support-enabled" : "form-opt-in-support-disabled"} /></Typography>}
                            editableContent={<OptInPolicy initIsDOptIn={form.double_opt_in} onChange={handleOptInPolicyChange} />}
                        />

                        <ResourceEdit
                            editable
                            title={t("Redirections")}
                            desc={t("Redirection page URL for opt-in and double opt-in submissions")}
                            support={<Stack gap={2}>
                                <Typography variant="body1">{t("form-post-redirect-url-desc", { url: isDefaultPostUrl ? t("general-default") : form.post_redirect_url })}</Typography>
                                <Typography variant="body1">{t("form-dOpt-in-redirect-url-desc", { url: isDefaultDoptInUrl ? t("general-default") : form.double_opt_in_redirect_url })}</Typography>
                            </Stack>}
                            editableContent={<Stack gap={6}>
                                <TextField
                                    name="post_redirect_url"
                                    label={t("form-post-redirect-url-label")}
                                    placeholder={t("general-url-placeholder")}
                                    defaultValue={isDefaultPostUrl ? "" : form.post_redirect_url}
                                    timeout={{
                                        callback: handleRedirectionChangeTimeout
                                    }}
                                    helperText={fieldStates.post_redirect_url.error && t(fieldStates.post_redirect_url.message || "")}
                                    error={fieldStates.post_redirect_url.error}
                                />
                                <TextField
                                    name="double_opt_in_redirect_url"
                                    label={t("form-dOpt-in-redirect-url-label")}
                                    placeholder={t("general-url-placeholder")}
                                    defaultValue={isDefaultDoptInUrl ? "" : form.double_opt_in_redirect_url}
                                    timeout={{
                                        callback: handleRedirectionChangeTimeout
                                    }}
                                    helperText={fieldStates.double_opt_in_redirect_url.error && t(fieldStates.double_opt_in_redirect_url.message || "")}
                                    error={fieldStates.double_opt_in_redirect_url.error}
                                />
                            </Stack>}
                        />

                        <ResourceEdit
                            title={t("general-link")}
                            desc={t("form-link-desc")}
                            support={form?.published_on > 0 ?
                                <ResourceCopy
                                    text={buildLink({ form, account: genericWrapper.account, user: genericWrapper.user })}
                                />

                                :
                                <Typography>{t("form-link-not-published")}</Typography>
                            }
                        />

                        <ResourceEdit
                            title={t("general-embed-snippet")}
                            desc={t("form-embed-snippet-desc")}
                            support={form?.published_on > 0 ?
                                <ResourceCopy
                                    isCodeBlock
                                    text={buildFromScript({ form }) || ""} />
                                :
                                <Typography>{t("form-embed-snippet-not-published")}</Typography>
                            }
                        />

                        <ResourceEdit
                            title={t("form-submission-url")}
                            desc={t("form-submission-url-desc")}
                            support={<ResourceCopy text={form.submission_url} />}
                        />
                    </Stack>

                    <ResourcePreview
                        content={previewContent}
                        actions={[
                            <Button
                                key="edit"
                                color="primary"
                                variant='contained'
                                size="small"
                                onClick={handleFormEdit} >
                                {t("general-edit-form")}
                            </Button>
                        ]}
                    />

                    <BeeEditor
                        hidden={!isEditing}
                        titleContent={<Typography variant="title" >{t("general-edit-form")}</Typography>}
                        onClose={handleOnEditorClose}
                        editorType={EEditorType.formDesigner}
                        initContent={form?.content?.json}
                        onSave={handleOnEditorSave}
                        onBrandingToggle={handleBrandingToggle}
                        contentContext={{
                            isSmart: form?.branding === "smart",
                            list: selectedList,
                            formAction: form?.submission_url
                        }}
                    />


                </Stack>
                }
            </LoadingContainer>
        }
    />
}